import jsmediatags from "jsmediatags";
import type { MetadataSource } from "@needle-drop/shared";
import { analyzeAudioBlob } from "./audioAnalysis";

export interface ParsedAudioMetadata {
  title: string;
  artist: string;
  album: string;
  year?: number | undefined;
  genres: string[];
  label: string;
  bpm?: number | undefined;
  bpmSource?: MetadataSource | undefined;
  bpmConfidence?: number | undefined;
  musicalKey: string;
  keySource?: MetadataSource | undefined;
  keyConfidence?: number | undefined;
  duration: number;
  coverArt?: Blob | undefined;
}

function asString(value: unknown): string {
  if (typeof value === "string") return value.trim();
  if (value && typeof value === "object" && "data" in value && typeof value.data === "string") return value.data.trim();
  return "";
}

function parsePicture(picture: unknown): Blob | undefined {
  if (!picture || typeof picture !== "object" || !("data" in picture) || !("format" in picture)) return undefined;
  const data = (picture as { data: number[] }).data;
  const format = (picture as { format: string }).format;
  return new Blob([new Uint8Array(data)], { type: format || "image/jpeg" });
}

function readTags(file: File): Promise<Record<string, unknown>> {
  return new Promise((resolve) => {
    jsmediatags.read(file, {
      onSuccess: ({ tags }) => resolve(tags),
      onError: () => resolve({}),
    });
  });
}

function readDuration(file: File): Promise<number> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const audio = new Audio();
    const finish = (duration: number) => { URL.revokeObjectURL(url); resolve(Number.isFinite(duration) ? duration : 0); };
    audio.preload = "metadata";
    audio.onloadedmetadata = () => finish(audio.duration);
    audio.onerror = () => finish(0);
    audio.src = url;
  });
}

export async function parseAudioMetadata(file: File): Promise<ParsedAudioMetadata> {
  const tags = await readTags(file);
  const baseName = file.name.replace(/\.mp3$/i, "");
  const fileParts = baseName.replace(/^\s*\d{1,3}\s*[._-]?\s*/, "").split(/\s+-\s+/);
  const fileArtist = fileParts.length > 1 ? fileParts.shift()?.trim() ?? "" : "";
  const fileTitle = fileParts.length ? fileParts.join(" - ").trim() : baseName;
  const yearValue = Number.parseInt(asString(tags.year), 10);
  const bpmValue = Number.parseFloat(asString(tags.TBPM ?? tags.bpm));
  const taggedKey = asString(tags.TKEY ?? tags.initialkey);
  let analysis: Awaited<ReturnType<typeof analyzeAudioBlob>> | undefined;
  if (!Number.isFinite(bpmValue) || !taggedKey) {
    try { analysis = await analyzeAudioBlob(file); } catch { /* ID3 import remains available when decoding fails. */ }
  }
  const duration = analysis?.duration ?? await readDuration(file);
  const genre = asString(tags.genre);
  const coverArt = parsePicture(tags.picture);
  return {
    title: asString(tags.title) || fileTitle,
    artist: asString(tags.artist) || fileArtist,
    album: asString(tags.album),
    ...(Number.isFinite(yearValue) ? { year: yearValue } : {}),
    genres: genre ? genre.split(/[;/,]/).map((item) => item.trim()).filter(Boolean) : [],
    label: asString(tags.TPUB ?? tags.publisher),
    ...(Number.isFinite(bpmValue) ? { bpm: bpmValue, bpmSource: "id3" as const, bpmConfidence: 1 } : analysis?.bpm ? { bpm: analysis.bpm, bpmSource: "analysis" as const, bpmConfidence: analysis.bpmConfidence } : {}),
    musicalKey: taggedKey || analysis?.musicalKey || "",
    ...(taggedKey ? { keySource: "id3" as const, keyConfidence: 1 } : analysis?.musicalKey ? { keySource: "analysis" as const, keyConfidence: analysis.keyConfidence } : {}),
    duration,
    ...(coverArt ? { coverArt } : {}),
  };
}

export async function hashFile(file: File): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}
